/**
 * ``FovLayer`` — non-visual React component that renders every platform's
 * **field-of-view** polygon as a GeoJSON fill + outline (PR5b inc3;
 * REQ-UI-070; ADR-032 §4.2). The first of the three SAPIENT geometry
 * overlays; coverage + obscuration (inc4) are its siblings, all three
 * sharing the same geometry store, toggle hook, and gated-effect lifecycle.
 *
 * Responsibilities:
 *   1. read the per-platform geometry from the shared geometry store
 *      (:func:`usePlatformGeometry`) — the store is fed by the status SSE
 *      bridge mounted in :file:`use-map-sse-bridges.ts` (this layer is
 *      READ-ONLY on the store, like :class:`DetectionLayer`);
 *   2. gate rendering behind the operator's ``fovVisible`` toggle AND a
 *      minimum zoom (the perf gate — see :const:`GEOMETRY_MIN_ZOOM`);
 *   3. attach / update the polygon layers in place when the gate is open;
 *      detach when it closes (toggle off, zoom out, or unmount).
 *
 * All of (2)+(3) — the zoom listener, the gate, the attach/detach plumbing —
 * is delegated to the shared :func:`useGeometryLayerEffect` so the three
 * geometry layers don't each repeat it (CLAUDE.md §4.4). This component only
 * binds the FoV-specific pieces: the ``fovVisible`` flag and the FoV
 * attach/detach.
 *
 * Cross-references:
 *   - REQ-UI-070 (off-by-default, zoom-gated geometry) / REQ-UI-002 (WebGL).
 *   - ADR-032 §4.2 (Status geometry data model).
 *   - CLAUDE.md §5.8 (perf discipline) / §8.1 (operator visibility) /
 *     §4.4 (no duplicated logic).
 *   - map/geometry-layer-effect.ts (the shared gated lifecycle).
 *   - map/geometry-store.ts · map/use-geometry-layers.ts · map/fov-layer-impl.ts.
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { usePlatformGeometry } from "@/map/geometry-store";
import { useGeometryVisibility } from "@/map/use-geometry-layers";
import { GEOMETRY_MIN_ZOOM, useGeometryLayerEffect } from "@/map/geometry-layer-effect";
import { attachFovLayer, detachFovLayer } from "@/map/fov-layer-impl";

// Re-export so inc3's ``import { GEOMETRY_MIN_ZOOM } from "@/map/fov-layer"``
// keeps resolving after the constant moved to the shared effect module.
export { GEOMETRY_MIN_ZOOM };

/** Props for :func:`FovLayer`. */
export interface FovLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
}

/**
 * Render + gate the field-of-view polygon layer. Returns no DOM
 * (WebGL-only).
 */
export function FovLayer({ map }: FovLayerProps): null {
  // Read-only: the geometry store is fed by the status-geometry SSE bridge
  // in useMapSseBridges (see module docstring). We subscribe + paint.
  const entries = usePlatformGeometry();
  // Visibility is the active Profile's map.visible_layers.fov, falling back
  // to false off-by-default (REQ-UI-089 / REQ-UI-070). Single source — the
  // per-session override layer was removed with the map cog menu (item 3a).
  // ``useGeometryVisibility`` reads {} with no provider, so the bare-render
  // unit tests keep the off-by-default baseline.
  const config = useGeometryVisibility();

  // The shared gated lifecycle: subscribe to zoom, attach when
  // ``fovVisible && zoom >= GEOMETRY_MIN_ZOOM`` else detach.
  useGeometryLayerEffect({
    map,
    entries,
    visible: config.fovVisible,
    attach: attachFovLayer,
    detach: detachFovLayer,
  });

  return null;
}
